import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import {
  CoverBox,
  TopContainer,
  TopTitleText,
  BasicDivFlexRight,
  BtnPurpleLgOutline,
} from 'components/atoms';
import MaterialGrid from 'components/organisms/commons/MaterialGrid';
import AssignmentSearchForm, {
  AssignmentSearchFormState,
} from 'components/molecules/managers/AssignmentSearchForm';
import { useQuery } from 'react-query';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Pagination } from '@material-ui/lab';
import { toast } from 'react-toastify';
import { makeExcel } from 'utils/parsers';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { getAssignmentColumns } from './columns';
import useDebounce from '../../../hooks/utils/useDebounce';
import {
  assignmentListAPI,
  fetchAssignmetList,
} from '../../../apis/assignments';

type StateType = {
  q: string;
  group: string;
  is_completed: string;
  page?: number;
};

const generateQueryString = (
  state: AssignmentSearchFormState,
  page: number,
  dQ: string,
): string => {
  const { group, is_completed } = state;
  let query: Record<string, string | boolean | number | Date> = {
    page,
    page_size: 10,
    is_active: true,
  };
  if (is_completed) {
    query = {
      ...query,
      complete: is_completed,
    };
  }
  if (dQ) {
    query = { ...query, q: dQ };
  }
  if (group) {
    query = { ...query, course__instructor__group: group };
  }
  return queryString.stringify(query);
};

const Assignments: React.FC = () => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const location = useLocation<StateType>();

  const [page, setPage] = useState(location?.state?.page || 1);
  const initialState = location.state || {
    q: '',
    is_completed: '',
    group: '',
  };
  const [state, setState] = useState(initialState);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const debouncedQ = useDebounce(state.q, 500);
  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };
  const query = generateQueryString(state, page, debouncedQ);
  const { data, isLoading, isError } = useQuery([query], fetchAssignmetList);

  useEffect(() => {
    if (isError) {
      setPage(1);
    }
  }, [isError, setPage]);
  return (
    <CoverBox>
      <TopContainer>
        <TopTitleText>
          {t('pagesManagersAssignments.taskManagement')}
        </TopTitleText>
        <BasicDivFlexRight>
          <BtnPurpleLgOutline
            onClick={async (): Promise<void> => {
              try {
                const response = await assignmentListAPI();
                const header = [
                  t('pagesManagersAssignments.group'),
                  t('pagesManagersAssignments.projectTitle'),
                  t('app.교수자'),
                  t('pagesManagersAssignments.task'),
                  t('pagesManagersAssignments.startingDate'),
                  t('pagesManagersAssignments.taskTerminationDate'),
                  t('pagesManagersAssignments.numberOfRegisteredStudents'),
                  t('pagesManagersAssignments.numberOfParticipatingStudents'),
                  t('pagesManagersAssignments.lectures'),
                  t('pagesManagersAssignments.taskStageSetting'),
                ];
                const rows = response.data.map((row) => [
                  row.group_name,
                  row.name,
                  row.instructor_name,
                  moment(row.created_at).format('YYYY-MM-DD'),
                  row.schedule_dates.start_date
                    ? moment(row.schedule_dates.start_date).format('YYYY-MM-DD')
                    : '',
                  row.schedule_dates.end_date
                    ? moment(row.schedule_dates.end_date).format('YYYY-MM-DD')
                    : '',
                  row.learners_count,
                  row.learners_checked_count,
                  row.course_description,
                  row.progresses_summary,
                ]);
                makeExcel(
                  [header, ...rows],
                  t('pagesManagersAssignments.administrator_taskManagement'),
                );
              } catch (e) {
                console.log(e);
                toast.warn(t('pagesManagersAssignments.thisIsTheWrongRequest'));
              }
            }}
          >
            <GetAppIcon style={{ verticalAlign: 'middle' }} />
            {t('pagesManagersAssignments.exportList')}
          </BtnPurpleLgOutline>
        </BasicDivFlexRight>
      </TopContainer>
      <AssignmentSearchForm state={state} onChange={onChange} />
      {React.useMemo(
        () =>
          language && (
            <MaterialGrid
              columns={getAssignmentColumns()}
              onRowClick={(e, rowData): void => {
                history.push(
                  `${url}/courses/${rowData?.course_id}/assignments/${rowData?.id}`,
                  {
                    ...state,
                    page,
                  },
                );
              }}
              isLoading={isLoading}
              components={{
                Pagination: (paginationProps): ReactElement => {
                  const styles = {
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 10,
                    paddingBottom: 10,
                  };
                  return (
                    <td style={styles}>
                      <Pagination
                        color="primary"
                        count={Math.ceil(paginationProps.count / 10)}
                        onChange={(e, newPage): void => {
                          setPage(newPage);
                        }}
                        page={page}
                      />
                    </td>
                  );
                },
              }}
              page={page}
              data={data?.data.results || []}
              totalCount={data?.data.count}
            />
          ),
        [page, history, url, isLoading, data, state, language],
      )}
    </CoverBox>
  );
};
export default Assignments;
